import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
  orderBy,
  limit,
  writeBatch,
} from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import useAuth from '../../hooks/useAuth';
import {
  UserGroupIcon,
  CalendarIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  BellIcon,
  CheckCircleIcon,
  ClockIcon,
  XMarkIcon,
  EyeIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  UserPlusIcon,
  FlagIcon,
  ShieldCheckIcon,
  CalendarDaysIcon,
  UsersIcon,
  ClipboardDocumentListIcon,
  MegaphoneIcon,
  CheckIcon,
  XMarkIcon as XMarkIconSolid,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
  TagIcon,
  BriefcaseIcon,
  UserCircleIcon,
  ClipboardIcon,
  InboxIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/outline';
import CoachesForms from '../CoachesForm/CoachesForms';
import ClientEditForm from '../team/ClientEditForm';
import EntrepreneurDashboard from '../entrepreneurProfile/EntrepreneurDashboard';
import { useNavigate } from 'react-router-dom';
import { normalizeBusinessStage } from '../../constants/businessStages';
import { MarbleLoader } from '../ui/LoadingStates';
import {
  MdPending,
  MdCheckCircle,
  MdError,
  MdLoop,
  MdLowPriority,
  MdPriorityHigh,
  MdOutlineAssignment,
  MdOutlineRateReview,
  MdOutlineChat,
  MdOutlineDescription,
  MdOutlinePeople,
  MdOutlineUpdate,
} from 'react-icons/md';

const Tooltip = ({ children, text, show }) => (
  <motion.div
    initial={{ opacity: 0, y: 4 }}
    animate={{ opacity: show ? 1 : 0, y: show ? 0 : 4 }}
    transition={{ duration: 0.15 }}
    className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2 whitespace-nowrap rounded bg-black px-2 py-1 text-xs text-white shadow-lg"
    style={{ pointerEvents: 'none' }}
  >
    {text}
  </motion.div>
);

const InterviewScheduling = () => {
  const { user } = useAuth();
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [interviewDate, setInterviewDate] = useState('');
  const [interviewTime, setInterviewTime] = useState('');
  const [interviewType, setInterviewType] = useState('video');

  useEffect(() => {
    fetchApplicants();
  }, [user]);

  const fetchApplicants = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const applicationsQuery = query(
        collection(firestore, 'applications'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(applicationsQuery);
      const applicantsData = snapshot.docs
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name:
              `${data.firstname || ''} ${data.lastname || ''}`.trim() ||
              data.applicantName ||
              'Unknown',
            email: data.email || data.userEmail || '',
            phone: data.phone || '',
            applicationDate: data.createdAt
              ? new Date(data.createdAt.toDate()).toLocaleDateString()
              : 'N/A',
            status: data.interviewStatus || data.status || 'pending',
            score: data.score || 0,
            notes: data.notes || data.additionalNotes || '',
            businessName: data.businessName || '',
            businessStage: data.businessStage || '',
            program: data.program || '',
            originalData: data,
          };
        })
        .filter(applicant =>
          ['pending', 'review', 'interview_scheduled', 'submitted'].includes(applicant.status)
        );
      if (applicantsData.length === 0) {
        const allApplicantsData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name:
              `${data.firstname || ''} ${data.lastname || ''}`.trim() ||
              data.applicantName ||
              'Unknown',
            email: data.email || data.userEmail || '',
            phone: data.phone || '',
            applicationDate: data.createdAt
              ? new Date(data.createdAt.toDate()).toLocaleDateString()
              : 'N/A',
            status: data.interviewStatus || data.status || 'pending',
            score: data.score || 0,
            notes: data.notes || data.additionalNotes || '',
            businessName: data.businessName || '',
            businessStage: data.businessStage || '',
            program: data.program || '',
            originalData: data,
          };
        });
        setApplicants(allApplicantsData);
      } else {
        setApplicants(applicantsData);
      }
    } catch (error) {
      try {
        const simpleQuery = query(collection(firestore, 'applications'));
        const snapshot = await getDocs(simpleQuery);
        const applicantsData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name:
              `${data.firstname || ''} ${data.lastname || ''}`.trim() ||
              data.applicantName ||
              'Unknown',
            email: data.email || data.userEmail || '',
            phone: data.phone || '',
            applicationDate: data.createdAt
              ? new Date(data.createdAt.toDate()).toLocaleDateString()
              : 'N/A',
            status: data.interviewStatus || data.status || 'pending',
            score: data.score || 0,
            notes: data.notes || data.additionalNotes || '',
            businessName: data.businessName || '',
            businessStage: data.businessStage || '',
            program: data.program || '',
            originalData: data,
          };
        });
        setApplicants(applicantsData);
      } catch (fallbackError) {
        toast.error('Failed to fetch applicants. Please check your connection.');
      }
    } finally {
      setLoading(false);
    }
  };

  const scheduleInterview = async () => {
    if (!interviewDate || !interviewTime) {
      toast.error('Please select date and time');
      return;
    }
    try {
      const applicationRef = doc(firestore, 'applications', selectedApplicant.id);
      await updateDoc(applicationRef, {
        interviewStatus: 'scheduled',
        interviewDate: interviewDate,
        interviewTime: interviewTime,
        interviewType: interviewType,
        scheduledBy: user.email,
        scheduledAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await addDoc(collection(firestore, 'interviews'), {
        applicationId: selectedApplicant.id,
        applicantName: selectedApplicant.name,
        applicantEmail: selectedApplicant.email,
        interviewDate: interviewDate,
        interviewTime: interviewTime,
        interviewType: interviewType,
        status: 'scheduled',
        scheduledBy: user.email,
        scheduledAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      });
      toast.success(
        `Interview scheduled for ${selectedApplicant.name} on ${interviewDate} at ${interviewTime}`
      );
      setShowScheduleModal(false);
      setSelectedApplicant(null);
      fetchApplicants();
    } catch (error) {
      toast.error('Failed to schedule interview');
    }
  };

  const getStatusColor = status => {
    switch (status) {
      case 'pending':
        return 'bg-amber-50 text-amber-600 border-amber-200';
      case 'review':
        return 'bg-blue-50 text-blue-600 border-blue-200';
      case 'interview_scheduled':
        return 'bg-emerald-50 text-emerald-600 border-emerald-200';
      case 'submitted':
        return 'bg-purple-50 text-purple-600 border-purple-200';
      default:
        return 'bg-gray-50 text-black border-gray-200';
    }
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl bg-white p-6 shadow-lg"
      >
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"></div>
            <p className="text-black/60">Loading applicants...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  const ViewProfile = async () => {};
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl bg-white p-4 shadow-lg md:p-6"
    >
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarIcon className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-black">Scheduling</h2>
        </div>
        <div className="flex gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => fetchApplicants()}
            className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
          >
            Refresh
          </motion.button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"></div>
            <p className="text-black/60">Loading applicants...</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {applicants.map((applicant, index) => (
            <motion.div
              key={applicant.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`rounded-lg border p-4 transition-all ${
                selectedApplicant?.id === applicant.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-black/20 hover:border-black/30'
              }`}
            >
              <div className="mb-3 flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-medium text-black">{applicant.name}</h3>
                  <p className="text-sm text-black/70">{applicant.email}</p>
                  {applicant.businessName && (
                    <p className="text-sm text-black/70">Business: {applicant.businessName}</p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {applicant.businessStage && (
                      <span className="rounded-full bg-blue-50 px-2 py-1 text-xs text-blue-600">
                        {applicant.businessStage}
                      </span>
                    )}
                    {applicant.program && (
                      <span className="rounded-full bg-indigo-50 px-2 py-1 text-xs text-indigo-600">
                        {applicant.program}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <span
                    className={`rounded-full border px-3 py-1 text-xs font-medium ${getStatusColor(applicant.status)}`}
                  >
                    {applicant.status.replace('_', ' ')}
                  </span>
                  <p className="mt-1 text-xs text-black/50">Applied: {applicant.applicationDate}</p>
                </div>
              </div>

              <div className="flex gap-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setSelectedApplicant(applicant);
                    setShowScheduleModal(true);
                  }}
                  className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                >
                  Schedule Interview
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedApplicant(applicant)}
                  className="rounded-lg border border-black/20 px-3 py-2 text-sm font-medium text-black transition-colors hover:bg-black/5"
                >
                  View Details
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Schedule Interview Modal */}
      {showScheduleModal && selectedApplicant && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setShowScheduleModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="w-full max-w-md rounded-lg bg-white p-6"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="mb-4 text-lg font-medium text-black">
              Schedule Interview for {selectedApplicant.name}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-black/70">Date</label>
                <input
                  type="date"
                  value={interviewDate}
                  onChange={e => setInterviewDate(e.target.value)}
                  className="w-full rounded-lg border border-black/20 p-3 focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600/20"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-black/70">Time</label>
                <input
                  type="time"
                  value={interviewTime}
                  onChange={e => setInterviewTime(e.target.value)}
                  className="w-full rounded-lg border border-black/20 p-3 focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600/20"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-black/70">Type</label>
                <select
                  value={interviewType}
                  onChange={e => setInterviewType(e.target.value)}
                  className="w-full rounded-lg border border-black/20 p-3 focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600/20"
                >
                  <option value="video">Video Call</option>
                  <option value="phone">Phone Call</option>
                  <option value="in-person">In-Person</option>
                </select>
              </div>
              <div className="flex gap-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={scheduleInterview}
                  className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
                >
                  Schedule
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowScheduleModal(false)}
                  className="flex-1 rounded-lg border border-black/20 px-4 py-2 text-black transition-colors hover:bg-black/5"
                >
                  Cancel
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

const LiveDashboard = () => {
  const { user } = useAuth();
  const [activeSessions, setActiveSessions] = useState([]);
  const [stats, setStats] = useState({
    totalSessions: 0,
    activeSessions: 0,
    totalParticipants: 0,
    averageAttendance: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const sessionsQuery = query(
        collection(firestore, 'sessions'),
        orderBy('startTime', 'desc'),
        limit(10)
      );

      const sessionsSnapshot = await getDocs(sessionsQuery);
      const sessionsData = sessionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      setActiveSessions(sessionsData);

      const applicationsQuery = query(collection(firestore, 'applications'));
      const clientsQuery = query(collection(firestore, 'clients'));
      const interviewsQuery = query(collection(firestore, 'interviews'));

      const [applicationsSnapshot, clientsSnapshot, interviewsSnapshot] = await Promise.all([
        getDocs(applicationsQuery),
        getDocs(clientsQuery),
        getDocs(interviewsQuery),
      ]);

      const totalSessions = sessionsSnapshot.size;
      const activeSessions = sessionsData.filter(session => session.status === 'active').length;
      const totalParticipants = clientsSnapshot.size;
      const totalInterviews = interviewsSnapshot.size;

      setStats({
        totalSessions,
        activeSessions,
        totalParticipants,
        averageAttendance:
          totalSessions > 0 ? Math.round((totalInterviews / totalSessions) * 100) : 0,
      });
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl bg-white p-6 shadow-lg"
      >
        <div className="flex items-center justify-center py-12">
          <MarbleLoader size="md" />
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl bg-white p-4 shadow-lg md:p-6"
    >
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ChartBarIcon className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-black">Live Dashboard</h2>
        </div>
        <div className="flex gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={fetchDashboardData}
            className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
          >
            Refresh
          </motion.button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <MarbleLoader size="md" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg bg-blue-50 p-4">
            <div className="flex items-center gap-2">
              <UserGroupIcon className="h-5 w-5 text-blue-600" />
              <span className="text-sm text-black/70">Total Applications</span>
            </div>
            <p className="text-2xl font-bold text-black">{stats.totalSessions}</p>
          </div>
          <div className="rounded-lg bg-emerald-50 p-4">
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-emerald-600" />
              <span className="text-sm text-black/70">Active Sessions</span>
            </div>
            <p className="text-2xl font-bold text-black">{stats.activeSessions}</p>
          </div>
          <div className="rounded-lg bg-amber-50 p-4">
            <div className="flex items-center gap-2">
              <UsersIcon className="h-5 w-5 text-purple-600" />
              <span className="text-sm text-black/70">Participants</span>
            </div>
            <p className="text-2xl font-bold text-black">{stats.totalParticipants}</p>
          </div>
          <div className="rounded-lg bg-yellow-50 p-4">
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-yellow-600" />
              <span className="text-sm text-black/70">Avg Attendance</span>
            </div>
            <p className="text-2xl font-bold text-black">{stats.averageAttendance}%</p>
          </div>
        </div>
      )}
    </motion.div>
  );
};

const FlaggingTool = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [newReport, setNewReport] = useState({
    participant: '',
    issue: '',
    severity: 'medium',
    description: '',
  });

  useEffect(() => {
    fetchReports();
  }, [user]);

  const fetchReports = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const reportsQuery = query(
        collection(firestore, 'issueReports'),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(reportsQuery);
      const reportsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      setReports(reportsData);
    } catch (error) {
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = severity => {
    switch (severity) {
      case 'high':
        return 'bg-red-100 text-red-700';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'low':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = status => 'bg-slate-100 text-slate-700';

  const updateReportStatus = async (id, status) => {
    try {
      const reportRef = doc(firestore, 'issueReports', id);
      await updateDoc(reportRef, {
        status,
        updatedAt: serverTimestamp(),
        updatedBy: user.email,
      });

      setReports(reports.map(report => (report.id === id ? { ...report, status } : report)));
      toast.success('Report status updated');
    } catch (error) {
      toast.error('Failed to update report status');
    }
  };

  const addReport = async () => {
    if (!newReport.participant || !newReport.issue || !newReport.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await addDoc(collection(firestore, 'issueReports'), {
        ...newReport,
        status: 'pending',
        reportedBy: user.email,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setNewReport({
        participant: '',
        issue: '',
        severity: 'medium',
        description: '',
      });
      setShowReportModal(false);
      fetchReports();
      toast.success('Report submitted successfully');
    } catch (error) {
      toast.error('Failed to submit report');
    }
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl bg-white p-6 shadow-lg"
      >
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"></div>
            <p className="text-gray-600">Loading reports...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl bg-white p-4 shadow-lg md:p-6"
    >
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FlagIcon className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-black">Flagging Tool</h2>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowReportModal(true)}
          className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
        >
          <ExclamationTriangleIcon className="h-4 w-4" />
          Report Issue
        </motion.button>
      </div>

      <div className="space-y-4">
        {reports.map((report, index) => (
          <motion.div
            key={report.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="rounded-lg border border-gray-200 p-4 transition-colors hover:border-gray-300"
          >
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h4 className="font-medium text-gray-800">{report.participant}</h4>
                <p className="text-sm text-gray-600">{report.issue}</p>
              </div>
              <div className="flex gap-2">
                <span
                  className={`rounded-full px-2 py-1 text-xs ${getSeverityColor(report.severity)}`}
                >
                  {report.severity}
                </span>
                <span className={`rounded-full px-2 py-1 text-xs ${getStatusColor(report.status)}`}>
                  {report.status}
                </span>
              </div>
            </div>

            <div className="mb-3 text-sm text-gray-600">
              <p>
                <strong>Reported by:</strong> {report.reportedBy}
              </p>
              <p>
                <strong>Date:</strong> {report.date}
              </p>
              <p>
                <strong>Description:</strong> {report.description}
              </p>
            </div>

            <div className="flex gap-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedReport(report)}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
              >
                <EyeIcon className="h-4 w-4" />
                View Details
              </motion.button>
              <select
                value={report.status}
                onChange={e => updateReportStatus(report.id, e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="pending">Pending</option>
                <option value="investigating">Investigating</option>
                <option value="resolved">Resolved</option>
                <option value="dismissed">Dismissed</option>
              </select>
            </div>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {selectedReport && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-2xl rounded-lg bg-white p-6"
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-800">Report Details</h3>
                <button
                  onClick={() => setSelectedReport(null)}
                  className="rounded-lg p-2 hover:bg-gray-100"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Participant</label>
                    <p className="text-gray-800">{selectedReport.participant}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Issue</label>
                    <p className="text-gray-800">{selectedReport.issue}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Severity</label>
                    <span
                      className={`inline-block rounded-full px-2 py-1 text-xs ${getSeverityColor(selectedReport.severity)}`}
                    >
                      {selectedReport.severity}
                    </span>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Status</label>
                    <span
                      className={`inline-block rounded-full px-2 py-1 text-xs ${getStatusColor(selectedReport.status)}`}
                    >
                      {selectedReport.status}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Description</label>
                  <p className="text-gray-800">{selectedReport.description}</p>
                </div>

                <div className="flex gap-2">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
                  >
                    Take Action
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedReport(null)}
                    className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    Close
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const NotificationCenter = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('all');

  useEffect(() => {
    fetchNotifications();
  }, [user]);

  const fetchNotifications = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const notificationsQuery = query(
        collection(firestore, 'notifications'),
        orderBy('createdAt', 'desc'),
        limit(50)
      );

      const snapshot = await getDocs(notificationsQuery);
      const notificationsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      setNotifications(notificationsData);
    } catch (error) {
      toast.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async id => {
    try {
      const notificationRef = doc(firestore, 'notifications', id);
      await updateDoc(notificationRef, {
        read: true,
        readAt: serverTimestamp(),
        readBy: user.email,
      });

      setNotifications(
        notifications.map(notification =>
          notification.id === id ? { ...notification, read: true } : notification
        )
      );
      toast.success('Notification marked as read');
    } catch (error) {
      toast.error('Failed to mark notification as read');
    }
  };

  const getTypeIcon = type => {
    switch (type) {
      case 'announcement':
        return <MegaphoneIcon className="h-5 w-5 text-blue-600" />;
      case 'system':
        return <ShieldCheckIcon className="h-5 w-5 text-gray-600" />;
      case 'reminder':
        return <BellIcon className="h-5 w-5 text-yellow-600" />;
      default:
        return <BellIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getPriorityColor = priority => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-700';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'low':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredNotifications =
    selectedType === 'all' ? notifications : notifications.filter(n => n.type === selectedType);

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl bg-white p-6 shadow-lg"
      >
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"></div>
            <p className="text-gray-600">Loading notifications...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl bg-white p-4 shadow-lg md:p-6"
    >
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BellIcon className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-800">Notification Center</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">
            {notifications.filter(n => !n.read).length} unread
          </span>
        </div>
      </div>

      <div className="mb-6 flex gap-2">
        {['all', 'announcement', 'system', 'reminder'].map(type => (
          <button
            key={type}
            onClick={() => setSelectedType(type)}
            className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              selectedType === type
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filteredNotifications.map((notification, index) => (
          <motion.div
            key={notification.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`rounded-lg border p-4 transition-all ${
              notification.read ? 'border-gray-200 bg-gray-50' : 'border-blue-200 bg-blue-50'
            }`}
          >
            <div className="mb-2 flex items-start justify-between">
              <div className="flex items-start gap-3">
                {getTypeIcon(notification.type)}
                <div className="flex-1">
                  <h4 className="font-medium text-gray-800">{notification.title}</h4>
                  <p className="text-sm text-gray-600">{notification.message}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`rounded-full px-2 py-1 text-xs ${getPriorityColor(notification.priority)}`}
                >
                  {notification.priority}
                </span>
                {!notification.read && <div className="h-2 w-2 rounded-full bg-blue-600"></div>}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {new Date(notification.date).toLocaleString()}
              </span>
              <div className="flex gap-2">
                {!notification.read && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => markAsRead(notification.id)}
                    className="rounded-lg bg-blue-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-blue-700"
                  >
                    Mark Read
                  </motion.button>
                )}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="rounded-lg border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  View Details
                </motion.button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {filteredNotifications.length === 0 && (
        <div className="py-8 text-center">
          <BellIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <p className="text-gray-500">No notifications found.</p>
        </div>
      )}
    </motion.div>
  );
};

const ClientDetails = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showEditClient, setShowEditClient] = useState(false);
  const [showProfilePopover, setShowProfilePopover] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
  }, [user]);

  const fetchClients = async () => {
    if (!user) return;

    setLoading(true);
    try {
      let clientsQuery;

      if (user.email) {
        const moderatorDoc = await getDoc(doc(firestore, 'moderator', user.uid));
        if (moderatorDoc.exists()) {
          clientsQuery = query(
            collection(firestore, 'clients'),
            where('assignedEmail', '==', user.email)
          );
        } else {
          clientsQuery = query(collection(firestore, 'clients'), where('email', '==', user.email));
        }
      } else {
        clientsQuery = collection(firestore, 'clients');
      }

      const snapshot = await getDocs(clientsQuery);
      const clientsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        businessStage: normalizeBusinessStage(doc.data().businessStage),
      }));

      setClients(clientsData);
    } catch (error) {
      toast.error('Failed to fetch client data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = status => 'bg-slate-100 text-slate-700';
  const getBusinessStageColor = stage => 'bg-slate-100 text-slate-700';

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl bg-white p-6 shadow-lg"
      >
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"></div>
            <p className="text-gray-600">Loading client data...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl bg-white p-4 shadow-lg md:p-6"
    >
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <UsersIcon className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-800">Client Details</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">
            {clients.length} client{clients.length !== 1 ? 's' : ''} found
          </span>
        </div>
      </div>

      {clients.length === 0 ? (
        <div className="py-12 text-center">
          <UsersIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">No clients found</h3>
          <p className="text-gray-600">
            {user?.email
              ? 'No clients are currently assigned to you.'
              : 'No client data available.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {clients.map((client, index) => (
            <motion.div
              key={client.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="relative flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-6 transition-all hover:border-blue-400 hover:shadow-lg"
            >
              <div className="absolute right-4 top-4 z-10">
                <ClientActionsPopover />
              </div>
              <div className="mb-2 flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-full border border-gray-200 bg-gray-100 text-2xl font-bold text-blue-700">
                  {(client.firstname || client.email)?.[0]?.toUpperCase() || 'C'}
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="truncate text-lg font-semibold text-black">
                    {client.firstname} {client.lastname}
                  </h4>
                  <p className="truncate text-sm text-gray-600">{client.email}</p>
                  {client.phone && <p className="truncate text-sm text-gray-500">{client.phone}</p>}
                </div>
              </div>
              <div className="mb-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                {client.program && (
                  <div>
                    <span className="text-xs font-medium text-gray-500">Program</span>
                    <div className="mt-1 inline-block rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                      {client.program}
                    </div>
                  </div>
                )}
                {client.businessName && (
                  <div>
                    <span className="text-xs font-medium text-gray-500">Business Name</span>
                    <div className="mt-1 text-sm text-gray-800">{client.businessName}</div>
                  </div>
                )}
                {client.createdAt && (
                  <div>
                    <span className="text-xs font-medium text-gray-500">Joined</span>
                    <div className="mt-1 text-sm text-gray-800">
                      {new Date(client.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                )}
                {client.assignedEmail && (
                  <div>
                    <span className="text-xs font-medium text-gray-500">Assigned To</span>
                    <div className="mt-1 text-sm text-gray-800">{client.assignedEmail}</div>
                  </div>
                )}
              </div>
              <div className="mb-2 flex flex-wrap gap-2">
                {client.status && (
                  <span className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700">
                    {client.status}
                  </span>
                )}
                {client.businessStage && (
                  <span className="rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                    {client.businessStage}
                  </span>
                )}
              </div>
              <div className="mt-2 flex gap-2 border-t border-gray-100 pt-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setSelectedClient(client);
                    setShowClientModal(true);
                  }}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700"
                >
                  <EyeIcon className="h-4 w-4" />
                  View Details
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                >
                  <ChatBubbleLeftRightIcon className="h-4 w-4" />
                  Contact
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {showClientModal && selectedClient && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => setShowClientModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-2xl rounded-xl bg-white p-8 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="absolute right-6 top-6 z-10">
                <ClientActionsPopover />
              </div>
              <div className="mb-6 flex items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-full border border-gray-200 bg-gray-100 text-2xl font-bold text-blue-700">
                  {(selectedClient.firstname || selectedClient.email)?.[0]?.toUpperCase() || 'C'}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-2xl font-bold text-black">
                    {selectedClient.firstname} {selectedClient.lastname}
                  </h3>
                  <p className="truncate text-base text-gray-600">{selectedClient.email}</p>
                  {selectedClient.phone && (
                    <p className="truncate text-sm text-gray-500">{selectedClient.phone}</p>
                  )}
                </div>
              </div>
              <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                {selectedClient.businessName && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Business Name</label>
                    <div className="mt-1 text-sm text-gray-800">{selectedClient.businessName}</div>
                  </div>
                )}
                {selectedClient.businessStage && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Business Stage</label>
                    <span className="mt-1 inline-block rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                      {selectedClient.businessStage}
                    </span>
                  </div>
                )}
                {selectedClient.status && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Status</label>
                    <span className="mt-1 inline-block rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700">
                      {selectedClient.status}
                    </span>
                  </div>
                )}
                {selectedClient.program && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Program</label>
                    <span className="mt-1 inline-block rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                      {selectedClient.program}
                    </span>
                  </div>
                )}
                {selectedClient.createdAt && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Joined</label>
                    <div className="mt-1 text-sm text-gray-800">
                      {new Date(selectedClient.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                )}
                {selectedClient.assignedEmail && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Assigned To</label>
                    <div className="mt-1 text-sm text-gray-800">{selectedClient.assignedEmail}</div>
                  </div>
                )}
              </div>
              {selectedClient.bio && (
                <div className="mb-4">
                  <label className="text-xs font-medium text-gray-500">Bio</label>
                  <div className="mt-1 text-sm text-gray-800">{selectedClient.bio}</div>
                </div>
              )}
              <div className="my-6 border-t border-gray-100"></div>
              <div className="flex gap-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowEditClient(true)}
                  className="flex-1 rounded-lg bg-blue-600 px-4 py-2 font-medium text-white shadow-sm transition-colors hover:bg-blue-700"
                >
                  Edit Client
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowProfilePopover(true)}
                  className="flex-1 rounded-lg bg-emerald-600 px-4 py-2 font-medium text-white shadow-sm transition-colors hover:bg-emerald-700"
                >
                  View Client Profile
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowClientModal(false)}
                  className="flex-1 rounded-lg border border-gray-300 px-4 py-2 font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                >
                  Close
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showEditClient && selectedClient && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowEditClient(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={e => e.stopPropagation()}
            >
              <ClientEditForm
                client={selectedClient}
                onClose={() => setShowEditClient(false)}
                onSaved={() => {
                  setShowEditClient(false);
                  toast.success('Client updated');
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showProfilePopover && selectedClient && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowProfilePopover(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-5xl overflow-hidden rounded-xl bg-white shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <EntrepreneurDashboard clientData={selectedClient} isReadOnly={true} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const TaskManager = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddTask, setShowAddTask] = useState(false);
  const [showEditTask, setShowEditTask] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium',
    dueDate: '',
    clientId: '',
    clientName: '',
    coachId: '',
    coachName: '',
    category: 'general',
    estimatedHours: '',
    notes: '',
    assignedTo: 'client',
  });

  useEffect(() => {
    if (!user || !user.email) {
      setTasks([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    const unsubscribes = [];

    const cleanup = () => {
      unsubscribes.forEach(unsub => {
        if (typeof unsub === 'function') {
          try {
            unsub();
          } catch (e) {}
        }
      });
      unsubscribes.length = 0;
    };

    const allTasksQuery = query(collection(firestore, 'tasks'), orderBy('createdAt', 'desc'));

    const unsubscribeAll = onSnapshot(
      allTasksQuery,
      snapshot => {
        const allTasks = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          taskType:
            doc.data().createdBy === user.email
              ? 'created'
              : doc.data().assignedToEmail === user.email
                ? 'assigned'
                : 'all',
        }));
        setTasks(allTasks);
        setLoading(false);
      },
      error => {
        const fallbackQuery = query(collection(firestore, 'tasks'));
        const unsubscribeFallback = onSnapshot(
          fallbackQuery,
          snapshot => {
            const allTasks = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              taskType:
                doc.data().createdBy === user.email
                  ? 'created'
                  : doc.data().assignedToEmail === user.email
                    ? 'assigned'
                    : 'all',
            }));
            setTasks(
              allTasks.sort((a, b) => {
                const aTime = a.createdAt?.toDate?.() || new Date(0);
                const bTime = b.createdAt?.toDate?.() || new Date(0);
                return bTime - aTime;
              })
            );
            setLoading(false);
          },
          error => {
            toast.error('Failed to fetch tasks.');
            setTasks([]);
            setLoading(false);
          }
        );
        unsubscribes.push(unsubscribeFallback);
      }
    );
    unsubscribes.push(unsubscribeAll);

    fetchClients();
    fetchCoaches();

    return cleanup;
  }, [user?.email]);

  const fetchClients = async () => {
    if (!user) return;

    try {
      const clientsQuery = query(
        collection(firestore, 'clients'),
        where('assignedEmail', '==', user.email)
      );

      const snapshot = await getDocs(clientsQuery);
      const clientsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      setClients(clientsData);
    } catch (error) {
      toast.error('Failed to fetch clients');
    }
  };

  const fetchCoaches = async () => {
    if (!user) return;

    try {
      const coachesQuery = query(collection(firestore, 'coaches'));
      const snapshot = await getDocs(coachesQuery);
      const coachesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      setCoaches(coachesData);
    } catch (error) {
      toast.error('Failed to fetch coaches');
    }
  };

  const getPriorityColor = priority => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-700';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'low':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = status => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'in-progress':
        return 'bg-blue-100 text-blue-700';
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'overdue':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getCategoryIcon = category => {
    switch (category) {
      case 'review':
        return <DocumentTextIcon className="h-4 w-4" />;
      case 'communication':
        return <ChatBubbleLeftRightIcon className="h-4 w-4" />;
      case 'documentation':
        return <ClipboardDocumentListIcon className="h-4 w-4" />;
      default:
        return <CheckIcon className="h-4 w-4" />;
    }
  };

  const updateTaskStatus = async (id, status) => {
    try {
      const taskRef = doc(firestore, 'tasks', id);
      await updateDoc(taskRef, {
        status,
        completedAt: status === 'completed' ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
        updatedBy: user.email,
      });

      setTasks(tasks.map(task => (task.id === id ? { ...task, status } : task)));
      toast.success('Task status updated');
    } catch (error) {
      toast.error('Failed to update task status');
    }
  };

  const deleteTask = async taskId => {
    try {
      await deleteDoc(doc(firestore, 'tasks', taskId));
      toast.success('Task deleted successfully');
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const addTask = async () => {
    if (!newTask.title || !newTask.description || !newTask.dueDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (newTask.assignedTo === 'client' && !newTask.clientId) {
      toast.error('Please select a client when assigning to client');
      return;
    }

    if (newTask.assignedTo === 'coach' && !newTask.coachId) {
      toast.error('Please select a coach when assigning to coach');
      return;
    }

    if (newTask.assignedTo === 'both' && (!newTask.clientId || !newTask.coachId)) {
      toast.error('Please select both client and coach when assigning to both');
      return;
    }

    try {
      const batch = writeBatch(firestore);
      let taskCount = 0;

      if (newTask.assignedTo === 'all_clients') {
        for (const client of clients) {
          const taskRef = doc(collection(firestore, 'tasks'));
          batch.set(taskRef, {
            ...newTask,
            clientName: `${client.firstname} ${client.lastname}`.trim(),
            assignedToEmail: client.email,
            assignedToName: `${client.firstname} ${client.lastname}`.trim(),
            userType: 'client',
            status: 'pending',
            createdBy: user.email,
            createdByName: user.email,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          taskCount++;
        }
      } else if (newTask.assignedTo === 'all_coaches') {
        for (const coach of coaches) {
          const taskRef = doc(collection(firestore, 'tasks'));
          batch.set(taskRef, {
            ...newTask,
            coachName: `${coach.firstname} ${coach.lastname}`.trim(),
            assignedToEmail: coach.email,
            assignedToName: `${coach.firstname} ${coach.lastname}`.trim(),
            userType: 'coach',
            status: 'pending',
            createdBy: user.email,
            createdByName: user.email,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          taskCount++;
        }
      } else {
        let clientName = newTask.clientName;
        let coachName = newTask.coachName;

        if (newTask.clientId) {
          const selectedClient = clients.find(client => client.id === newTask.clientId);
          clientName = selectedClient
            ? `${selectedClient.firstname} ${selectedClient.lastname}`.trim()
            : newTask.clientName;
        }

        if (newTask.coachId) {
          const selectedCoach = coaches.find(coach => coach.id === newTask.coachId);
          coachName = selectedCoach
            ? `${selectedCoach.firstname} ${selectedCoach.lastname}`.trim()
            : newTask.coachName;
        }

        let assignedToEmail = '';
        let assignedToName = '';
        let userType = '';

        switch (newTask.assignedTo) {
          case 'client':
            assignedToEmail = clients.find(c => c.id === newTask.clientId)?.email || '';
            assignedToName = clientName;
            userType = 'client';
            break;
          case 'coach':
            assignedToEmail = coaches.find(c => c.id === newTask.coachId)?.email || '';
            assignedToName = coachName;
            userType = 'coach';
            break;
          case 'both':
            const client = clients.find(c => c.id === newTask.clientId);
            const coach = coaches.find(c => c.id === newTask.coachId);

            if (client) {
              const clientTaskRef = doc(collection(firestore, 'tasks'));
              batch.set(clientTaskRef, {
                ...newTask,
                clientName,
                assignedToEmail: client.email,
                assignedToName: clientName,
                userType: 'client',
                status: 'pending',
                createdBy: user.email,
                createdByName: user.email,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              });
              taskCount++;
            }

            if (coach) {
              const coachTaskRef = doc(collection(firestore, 'tasks'));
              batch.set(coachTaskRef, {
                ...newTask,
                coachName,
                assignedToEmail: coach.email,
                assignedToName: coachName,
                userType: 'coach',
                status: 'pending',
                createdBy: user.email,
                createdByName: user.email,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              });
              taskCount++;
            }
            break;
        }

        if (newTask.assignedTo !== 'both') {
          const taskRef = doc(collection(firestore, 'tasks'));
          batch.set(taskRef, {
            ...newTask,
            clientName,
            coachName,
            assignedToEmail,
            assignedToName,
            userType,
            status: 'pending',
            createdBy: user.email,
            createdByName: user.email,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          taskCount++;
        }
      }

      await batch.commit();
      setShowAddTask(false);
      resetTaskForm();
      toast.success(`Successfully created ${taskCount} task(s)`);
    } catch (error) {
      toast.error('Failed to add task');
    }
  };

  const editTask = async () => {
    if (!selectedTask || !newTask.title || !newTask.description || !newTask.dueDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      let clientName = newTask.clientName;
      if (newTask.clientId) {
        const selectedClient = clients.find(client => client.id === newTask.clientId);
        clientName = selectedClient
          ? `${selectedClient.firstname} ${selectedClient.lastname}`.trim()
          : newTask.clientName;
      }

      const taskRef = doc(firestore, 'tasks', selectedTask.id);
      await updateDoc(taskRef, {
        ...newTask,
        clientName,
        updatedAt: serverTimestamp(),
        updatedBy: user.email,
      });

      resetTaskForm();
      setShowEditTask(false);
      setSelectedTask(null);
      toast.success('Task updated successfully');
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const resetTaskForm = () => {
    setNewTask({
      title: '',
      description: '',
      priority: 'medium',
      dueDate: '',
      clientId: '',
      clientName: '',
      coachId: '',
      coachName: '',
      category: 'general',
      estimatedHours: '',
      notes: '',
      assignedTo: 'client',
    });
  };

  const handleEditTask = task => {
    setSelectedTask(task);
    setNewTask({
      title: task.title || '',
      description: task.description || '',
      priority: task.priority || 'medium',
      dueDate: task.dueDate || '',
      clientId: task.clientId || '',
      clientName: task.clientName || '',
      coachId: task.coachId || '',
      coachName: task.coachName || '',
      category: task.category || 'general',
      estimatedHours: task.estimatedHours || '',
      notes: task.notes || '',
      assignedTo: task.assignedTo || 'client',
    });
    setShowEditTask(true);
  };

  const stats = [
    {
      label: 'Total Tasks',
      value: tasks.length,
      icon: <ClipboardDocumentListIcon className="h-6 w-6 text-blue-600" />,
      bg: 'bg-blue-100',
    },
    {
      label: 'Pending',
      value: tasks.filter(t => t.status === 'pending').length,
      icon: <ClockIcon className="h-6 w-6 text-yellow-600" />,
      bg: 'bg-yellow-100',
    },
    {
      label: 'In Progress',
      value: tasks.filter(t => t.status === 'in-progress').length,
      icon: <ChartBarIcon className="h-6 w-6 text-blue-600" />,
      bg: 'bg-blue-100',
    },
    {
      label: 'Completed',
      value: tasks.filter(t => t.status === 'completed').length,
      icon: <CheckCircleIcon className="h-6 w-6 text-green-600" />,
      bg: 'bg-green-100',
    },
  ];

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl bg-white p-6 shadow-lg"
      >
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"></div>
            <p className="text-gray-600">Loading tasks...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl bg-white p-6 shadow-lg"
    >
      <div className="mb-6 flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-3">
            <ClipboardDocumentListIcon className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-800">All Tasks</h2>
          </div>
          <span className="text-sm text-gray-500">
            View all tasks created by and assigned to anyone.
          </span>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowAddTask(true)}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4" />
          Add Task
        </motion.button>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, idx) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className={`rounded-lg ${stat.bg} p-4`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
              {stat.icon}
            </div>
          </motion.div>
        ))}
      </div>

      <div className="space-y-4">
        {tasks.map((task, index) => (
          <motion.div
            key={task.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="group rounded-xl border border-gray-200 bg-white p-6 transition-all hover:border-blue-300 hover:shadow-lg"
          >
            <div className="mb-4 flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="mt-1 rounded-lg bg-blue-100 p-2">
                  {getCategoryIcon(task.category)}
                </div>
                <div className="flex-1">
                  <div className="mb-2 flex items-center gap-3">
                    <h4 className="text-lg font-semibold text-gray-800">{task.title}</h4>
                    <div className="flex gap-2">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-medium ${getPriorityColor(task.priority)}`}
                      >
                        {task.priority}
                      </span>
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-medium ${getStatusColor(task.status)}`}
                      >
                        {task.status}
                      </span>
                    </div>
                  </div>
                  <p className="mb-3 text-gray-600">{task.description}</p>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {task.clientName && (
                      <div className="flex items-center gap-2">
                        <UserIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          <span className="font-medium">Client:</span> {task.clientName}
                        </span>
                      </div>
                    )}
                    {task.coachName && (
                      <div className="flex items-center gap-2">
                        <UserIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          <span className="font-medium">Coach:</span> {task.coachName}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <UserIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        <span className="font-medium">Assigned To:</span>{' '}
                        {task.assignedToName || task.assignedToEmail || task.assignedTo}
                      </span>
                    </div>
                    {task.createdBy && (
                      <div className="flex items-center gap-2">
                        <UserIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          <span className="font-medium">Created By:</span>{' '}
                          {task.createdByName || task.createdBy}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        <span className="font-medium">Due:</span>{' '}
                        {new Date(task.dueDate).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <TagIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        <span className="font-medium">Category:</span> {task.category}
                      </span>
                    </div>
                    {task.estimatedHours && (
                      <div className="flex items-center gap-2">
                        <ClockIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          <span className="font-medium">Est. Hours:</span> {task.estimatedHours}
                        </span>
                      </div>
                    )}
                  </div>

                  {task.notes && (
                    <div className="mt-3 rounded-lg bg-gray-50 p-3">
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Notes:</span> {task.notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
              <select
                value={task.status}
                onChange={e => updateTaskStatus(task.id, e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="pending">
                  <MdPending className="mr-1 inline" /> Pending
                </option>
                <option value="in-progress">
                  <MdLoop className="mr-1 inline" /> In Progress
                </option>
                <option value="completed">
                  <MdCheckCircle className="mr-1 inline" /> Completed
                </option>
                <option value="overdue">
                  <MdError className="mr-1 inline" /> Overdue
                </option>
              </select>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleEditTask(task)}
                className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-gray-400 hover:bg-gray-50"
              >
                <PencilIcon className="h-4 w-4" />
                Edit Task
              </motion.button>

              {task.taskType === 'created' && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => deleteTask(task.id)}
                  className="flex items-center gap-2 rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 transition-colors hover:border-red-400 hover:bg-red-50"
                >
                  <TrashIcon className="h-4 w-4" />
                  Delete
                </motion.button>
              )}

              {task.status !== 'completed' && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => updateTaskStatus(task.id, 'completed')}
                  className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700"
                >
                  <CheckIcon className="h-4 w-4" />
                  Mark as Done
                </motion.button>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {tasks.length === 0 && (
        <div className="py-12 text-center">
          <ClipboardDocumentListIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">
            You have no tasks assigned to you.
          </h3>
          <p className="text-gray-600">Tasks assigned to you will appear here.</p>
        </div>
      )}

      <AnimatePresence>
        {showAddTask && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl"
            >
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">Create New Task</h3>
                  <p className="text-sm text-gray-600">Add a new task to your workflow</p>
                </div>
                <button
                  onClick={() => setShowAddTask(false)}
                  className="rounded-lg p-2 hover:bg-gray-100"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Task Title */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Task Title *
                  </label>
                  <input
                    type="text"
                    value={newTask.title}
                    onChange={e => setNewTask({ ...newTask, title: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Enter a descriptive task title"
                  />
                </div>

                {/* Task Description */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Description *
                  </label>
                  <textarea
                    value={newTask.description}
                    onChange={e => setNewTask({ ...newTask, description: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    rows="4"
                    placeholder="Provide detailed description of the task"
                  />
                </div>

                {/* Client Selection */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Assign to Client
                  </label>
                  <div className="space-y-2">
                    <select
                      value={newTask.clientId}
                      onChange={e => {
                        const clientId = e.target.value;
                        const selectedClient = clients.find(client => client.id === clientId);
                        setNewTask({
                          ...newTask,
                          clientId,
                          clientName: selectedClient
                            ? `${selectedClient.firstname} ${selectedClient.lastname}`.trim()
                            : '',
                        });
                      }}
                      className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Select a client (optional)</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.firstname} {client.lastname} - {client.email}
                        </option>
                      ))}
                    </select>
                    {clients.length === 0 && (
                      <p className="text-sm text-gray-500">
                        No clients available. You can still create a general task.
                      </p>
                    )}
                  </div>
                </div>

                {/* Task Details Grid */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Priority</label>
                    <select
                      value={newTask.priority}
                      onChange={e => setNewTask({ ...newTask, priority: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="low">
                        <MdLowPriority className="mr-1 inline" /> Low
                      </option>
                      <option value="medium">
                        <MdOutlineUpdate className="mr-1 inline" /> Medium
                      </option>
                      <option value="high">
                        <MdPriorityHigh className="mr-1 inline" /> High
                      </option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Category</label>
                    <select
                      value={newTask.category}
                      onChange={e => setNewTask({ ...newTask, category: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="general">
                        <MdOutlineAssignment className="mr-1 inline" /> General
                      </option>
                      <option value="review">
                        <MdOutlineRateReview className="mr-1 inline" /> Review
                      </option>
                      <option value="communication">
                        <MdOutlineChat className="mr-1 inline" /> Communication
                      </option>
                      <option value="documentation">
                        <MdOutlineDescription className="mr-1 inline" /> Documentation
                      </option>
                      <option value="meeting">
                        <MdOutlinePeople className="mr-1 inline" /> Meeting
                      </option>
                      <option value="followup">
                        <MdOutlineUpdate className="mr-1 inline" /> Follow-up
                      </option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Estimated Hours
                    </label>
                    <input
                      type="number"
                      min="0.5"
                      step="0.5"
                      value={newTask.estimatedHours}
                      onChange={e => setNewTask({ ...newTask, estimatedHours: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="2.5"
                    />
                  </div>
                </div>

                {/* Due Date */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Due Date *</label>
                  <input
                    type="date"
                    value={newTask.dueDate}
                    onChange={e => setNewTask({ ...newTask, dueDate: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                {/* Additional Notes */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Additional Notes
                  </label>
                  <textarea
                    value={newTask.notes}
                    onChange={e => setNewTask({ ...newTask, notes: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    rows="3"
                    placeholder="Any additional notes or context..."
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={addTask}
                    className="flex-1 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-3 font-medium text-white shadow-lg transition-all hover:from-blue-700 hover:to-blue-800 hover:shadow-xl"
                  >
                    <PlusIcon className="mr-2 inline h-5 w-5" />
                    Create Task
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowAddTask(false);
                      resetTaskForm();
                    }}
                    className="flex-1 rounded-lg border border-gray-300 px-6 py-3 font-medium text-gray-700 transition-all hover:bg-gray-50"
                  >
                    Cancel
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Task Modal */}
      <AnimatePresence>
        {showEditTask && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl"
            >
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">Edit Task</h3>
                  <p className="text-sm text-gray-600">Update task details and progress</p>
                </div>
                <button
                  onClick={() => {
                    setShowEditTask(false);
                    setSelectedTask(null);
                    resetTaskForm();
                  }}
                  className="rounded-lg p-2 hover:bg-gray-100"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Task Title */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Task Title *
                  </label>
                  <input
                    type="text"
                    value={newTask.title}
                    onChange={e => setNewTask({ ...newTask, title: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Enter a descriptive task title"
                  />
                </div>

                {/* Task Description */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Description *
                  </label>
                  <textarea
                    value={newTask.description}
                    onChange={e => setNewTask({ ...newTask, description: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    rows="4"
                    placeholder="Provide detailed description of the task"
                  />
                </div>

                {/* Client Selection */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Assign to Client
                  </label>
                  <div className="space-y-2">
                    <select
                      value={newTask.clientId}
                      onChange={e => {
                        const clientId = e.target.value;
                        const selectedClient = clients.find(client => client.id === clientId);
                        setNewTask({
                          ...newTask,
                          clientId,
                          clientName: selectedClient
                            ? `${selectedClient.firstname} ${selectedClient.lastname}`.trim()
                            : '',
                        });
                      }}
                      className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Select a client (optional)</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.firstname} {client.lastname} - {client.email}
                        </option>
                      ))}
                    </select>
                    {clients.length === 0 && (
                      <p className="text-sm text-gray-500">
                        No clients available. You can still create a general task.
                      </p>
                    )}
                  </div>
                </div>

                {/* Task Details Grid */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Priority</label>
                    <select
                      value={newTask.priority}
                      onChange={e => setNewTask({ ...newTask, priority: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="low">
                        <MdLowPriority className="mr-1 inline" /> Low
                      </option>
                      <option value="medium">
                        <MdOutlineUpdate className="mr-1 inline" /> Medium
                      </option>
                      <option value="high">
                        <MdPriorityHigh className="mr-1 inline" /> High
                      </option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Category</label>
                    <select
                      value={newTask.category}
                      onChange={e => setNewTask({ ...newTask, category: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="general">
                        <MdOutlineAssignment className="mr-1 inline" /> General
                      </option>
                      <option value="review">
                        <MdOutlineRateReview className="mr-1 inline" /> Review
                      </option>
                      <option value="communication">
                        <MdOutlineChat className="mr-1 inline" /> Communication
                      </option>
                      <option value="documentation">
                        <MdOutlineDescription className="mr-1 inline" /> Documentation
                      </option>
                      <option value="meeting">
                        <MdOutlinePeople className="mr-1 inline" /> Meeting
                      </option>
                      <option value="followup">
                        <MdOutlineUpdate className="mr-1 inline" /> Follow-up
                      </option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Estimated Hours
                    </label>
                    <input
                      type="number"
                      min="0.5"
                      step="0.5"
                      value={newTask.estimatedHours}
                      onChange={e => setNewTask({ ...newTask, estimatedHours: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="2.5"
                    />
                  </div>
                </div>

                {/* Due Date */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Due Date *</label>
                  <input
                    type="date"
                    value={newTask.dueDate}
                    onChange={e => setNewTask({ ...newTask, dueDate: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                {/* Additional Notes */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Additional Notes
                  </label>
                  <textarea
                    value={newTask.notes}
                    onChange={e => setNewTask({ ...newTask, notes: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    rows="3"
                    placeholder="Any additional notes or context..."
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={editTask}
                    className="flex-1 rounded-lg bg-gradient-to-r from-green-600 to-green-700 px-6 py-3 font-medium text-white shadow-lg transition-all hover:from-green-700 hover:to-green-800 hover:shadow-xl"
                  >
                    <PencilIcon className="mr-2 inline h-5 w-5" />
                    Update Task
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowEditTask(false);
                      setSelectedTask(null);
                      resetTaskForm();
                    }}
                    className="flex-1 rounded-lg border border-gray-300 px-6 py-3 font-medium text-gray-700 transition-all hover:bg-gray-50"
                  >
                    Cancel
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const ClientActionsPopover = () => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState('');
  const popoverRef = useRef(null);
  const buttonRef = useRef(null);

  const actions = [
    {
      icon: <BriefcaseIcon className="h-6 w-6" />,
      label: 'Business Profile',
      onClick: () => {},
    },
    {
      icon: <UserCircleIcon className="h-6 w-6" />,
      label: 'Personality Profile',
      onClick: () => {},
    },
    {
      icon: <ClipboardDocumentListIcon className="h-6 w-6" />,
      label: 'Project Plan',
      onClick: () => {},
    },
    {
      icon: <UserGroupIcon className="h-6 w-6" />,
      label: 'Coach & Mentors Reports',
      onClick: () => {},
    },
    {
      icon: <ClipboardIcon className="h-6 w-6" />,
      label: 'Client Task Manager',
      onClick: () => {},
    },
    {
      icon: <InboxIcon className="h-6 w-6" />,
      label: 'Submissions/Documents',
      onClick: () => {},
    },
  ];

  useEffect(() => {
    function handleClick(e) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    function handleKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', handleClick);
      document.addEventListener('keydown', handleKey);
    }
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <div className="relative inline-block">
      <motion.button
        ref={buttonRef}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setOpen(v => !v)}
        className="rounded-full bg-gray-100 p-2 text-blue-600 hover:bg-blue-100"
        title="More Actions"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <EllipsisVerticalIcon className="h-5 w-5" />
      </motion.button>
      <AnimatePresence>
        {open && (
          <motion.div
            ref={popoverRef}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18 }}
            className="absolute right-0 z-50 mt-2 flex w-64 flex-col items-center rounded-xl border border-gray-100 bg-white p-5 shadow-2xl"
            style={{ minWidth: '16rem' }}
          >
            <div className="mb-4 flex w-full items-center justify-between">
              <h3 className="text-base font-semibold text-black">Client Actions</h3>
              <button
                onClick={() => setOpen(false)}
                className="rounded-full p-2 text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="grid w-full grid-cols-3 justify-items-center gap-4">
              {actions.map(action => (
                <div key={action.label} className="relative flex flex-col items-center">
                  <motion.button
                    whileHover={{ scale: 1.15 }}
                    whileTap={{ scale: 0.95 }}
                    onMouseEnter={() => setHovered(action.label)}
                    onMouseLeave={() => setHovered('')}
                    onClick={action.onClick}
                    className="rounded-full bg-gray-100 p-3 text-blue-600 hover:bg-blue-100 focus:outline-none"
                    aria-label={action.label}
                    type="button"
                  >
                    {action.icon}
                  </motion.button>
                  {hovered === action.label && <Tooltip text={action.label} show={true} />}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ModeratorCRM = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({
    totalApplicants: 0,
    pendingInterviews: 0,
    activeSessions: 0,
    openReports: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, [user]);

  const fetchStats = async () => {
    if (!user || !user.email) {
      setStats({
        totalApplicants: 0,
        pendingInterviews: 0,
        activeSessions: 0,
        openReports: 0,
      });
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const clientsQuery = query(
        collection(firestore, 'clients'),
        where('assignedEmail', '==', user.email)
      );
      const clientsSnapshot = await getDocs(clientsQuery);
      const totalApplicants = clientsSnapshot.size;
      const [interviewsSnapshot, sessionsSnapshot, reportsSnapshot] = await Promise.all([
        getDocs(collection(firestore, 'interviews')),
        getDocs(collection(firestore, 'sessions')),
        getDocs(collection(firestore, 'issueReports')),
      ]);
      const pendingInterviews = interviewsSnapshot.docs.filter(
        doc => doc.data().status === 'pending'
      ).length;
      const activeSessions = sessionsSnapshot.docs.filter(
        doc => doc.data().status === 'active'
      ).length;
      const openReports = reportsSnapshot.docs.filter(
        doc => doc.data().status === 'pending'
      ).length;
      setStats({
        totalApplicants,
        pendingInterviews,
        activeSessions,
        openReports,
      });
    } catch (error) {
      toast.error('Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'dashboard', name: 'Live Dashboard', icon: ChartBarIcon },
    { id: 'clients', name: 'Client Details', icon: UsersIcon },
    { id: 'tasks', name: 'Task Manager', icon: ClipboardDocumentListIcon },
    { id: 'coaches', name: 'Coaches & Mentors', icon: UserGroupIcon },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-black">Business Advisor Dashboard</h1>
          <p className="text-black/70">
            Manage applications, schedule interviews, and monitor system activity
          </p>
        </div>

        <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-xl bg-white p-6 shadow-lg"
          >
            <div className="flex items-center gap-3">
              <UserGroupIcon className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-sm text-black">Assigned Clients</p>
                <p className="text-2xl font-bold text-black">{stats.totalApplicants}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="rounded-xl bg-white p-6 shadow-lg"
          >
            <div className="flex items-center gap-3">
              <CalendarIcon className="h-8 w-8 text-emerald-600" />
              <div>
                <p className="text-sm text-black">Scheduled Interviews</p>
                <p className="text-2xl font-bold text-black">{stats.pendingInterviews}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="rounded-xl bg-white p-6 shadow-lg"
          >
            <div className="flex items-center gap-3">
              <FlagIcon className="h-8 w-8 text-amber-600" />
              <div>
                <p className="text-sm text-black">Open Reports</p>
                <p className="text-2xl font-bold text-black">{stats.openReports}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="rounded-xl bg-white p-6 shadow-lg"
          >
            <div className="flex items-center gap-3">
              <BellIcon className="h-8 w-8 text-indigo-600" />
              <div>
                <p className="text-sm text-black">Notifications</p>
                <p className="text-2xl font-bold text-black">{stats.notifications}</p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mb-6 flex flex-wrap gap-2">
          {tabs.map(tab => (
            <motion.button
              key={tab.id}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'border border-black/20 bg-white text-black hover:bg-black/5 hover:text-black'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              {tab.name}
            </motion.button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {activeTab === 'scheduling' && <InterviewScheduling />}
            {activeTab === 'dashboard' && <LiveDashboard />}
            {activeTab === 'flagging' && <FlaggingTool />}
            {activeTab === 'notifications' && <NotificationCenter />}
            {activeTab === 'clients' && <ClientDetails />}
            {activeTab === 'tasks' && <TaskManager />}
            {activeTab === 'coaches' && <CoachesForms />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ModeratorCRM;
